<template>
  <div id="sales-by-products" class="fill-height align-start">
    <i-overlay v-if="initialLoading" text="Loading your reports data..." />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        v-if="customiseReportDialog"
        v-model="customiseReportDialog"
        max-width="750px"
        persistent
        @click:outside="customiseReportDialog = false"
        @keydown.esc="customiseReportDialog = false"
      >
        <i-customise-sales-report
          :reportFields="reportFields"
          :reportType="'BY_PRODUCTS'"
          @cancel="customiseReportDialog = false"
          @customise="customiseReport"
        ></i-customise-sales-report>
      </v-dialog>

      <v-col>
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-card class="i-card mt-3 pt-2 pb-4">
            <div class="text-center">
              <span
                >To use this feature you must access your Supplier portal from a
                Desktop device</span
              >
            </div>
            <div class="text-center">
              <v-btn
                rounded
                class="g-full-width i-btn-block-secondary mt-2"
                :to="{ name: 'reports' }"
              >
                Reports
              </v-btn>
            </div>
          </v-card>
        </template>

        <template v-else>
          <v-row>
            <i-sales-report-exclusions-info />
            <v-spacer></v-spacer>
            <v-col cols="12" sm="4" md="3" lg="2">
              <i-instructions
                instructionKey="reports/sales-by-products"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="4" md="3" lg="2">
              <v-btn
                class="g-full-width i-btn-block-primary c-large"
                @click="customiseReportDialog = true"
                >Customise Report</v-btn
              >
            </v-col>

            <v-spacer></v-spacer>

            <v-col cols="12" sm="3" lg="2">
              <v-card class="p-customise-card">
                <div class="p-list-div">
                  <div class="p-list-div-icon" v-tooltip="'Aggregation Period'">
                    <v-icon class="p-list-icon">mdi-calendar-range</v-icon>
                  </div>
                  <v-select
                    attach
                    class="date-picker"
                    dense
                    hide-details
                    :items="aggregationPeriods"
                    flat
                    solo
                    :value="reportFields.aggregationPeriod"
                    @input="(period) => handleAggregationPeriod(period)"
                  >
                  </v-select>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="3" lg="2">
              <v-card class="p-customise-card">
                <div class="p-list-div">
                  <div class="p-list-div-icon" v-tooltip="valuesAsTooltip">
                    <v-icon class="p-list-icon">mdi-package-variant</v-icon>
                  </div>
                  <v-select
                    attach
                    class="date-picker"
                    dense
                    flat
                    hide-details
                    :items="valuesAsItems"
                    solo
                    :value="reportFields.valuesAs"
                    @input="(as) => handleValuesAs(as)"
                  >
                  </v-select>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-spacer></v-spacer>

            <v-col cols="12" sm="3" lg="2">
              <div>
                <p class="g-bold mb-0">Date Range:</p>
                <span>{{ `${dateFromDisplay} - ${dateToDisplay}` }}</span>
              </div>
            </v-col>

            <v-col cols="12" sm="3" lg="2" class="text-right">
              <v-btn
                class="i-btn-block-primary c-large"
                :disabled="loading"
                :loading="exporting"
                @click="exportReport"
                >Export Results<v-icon class="ml-1"
                  >mdi-file-excel-outline</v-icon
                ></v-btn
              >
            </v-col>
          </v-row>

          <i-progress-linear v-if="loading" />

          <v-row dense>
            <v-col cols="12">
              <v-data-table
                class="elevation-1 i-data-table p-report-table"
                :headers="tableHeaders"
                :items="tableItems"
                :items-per-page="-1"
                item-key="name"
                hide-default-footer
                no-results-text="No data found for this report."
              >
                <template v-slot:[`body.append`]="{ headers }">
                  <tr>
                    <td v-for="(header, i) in headers" :key="i">
                      {{ tableItemsGrandTotal[header.value] }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { mapActions, mapGetters } from 'vuex'
import iCustomiseSalesReport from '@/modules/reports/components/CustomiseSalesReport'
import iOverlay from '@/components/Overlay'
import iProgressLinear from '@/components/ProgressLinear'
import {
  fetchSalesByProducts,
  fetchSalesByProductsAsExcel
} from '@/services/reports'
import iInstructions from '@/components/Instructions'
import iSalesReportExclusionsInfo from '@/modules/reports/components/SalesReportExclusionsInfo'

const AGGREGATION_PERIODS = [
  { text: 'Week', value: 'WEEK' },
  { text: 'Month', value: 'MONTH' },
  { text: 'Quarter', value: 'QUARTER' },
  { text: 'Year', value: 'YEAR' }
]

const DAYS_BACK = 30

const REVENUE_VALUE = 'REVENUE'
const CARTONS_VALUE = 'CARTONS'

const REPORT_FIELDS = {
  aggregationPeriod: AGGREGATION_PERIODS.filter((p) => p.value === 'MONTH')[0]
    ?.value,
  bannerGroupIds: [],
  dateFrom: '',
  dateTo: '',
  deliveryAddressStates: [],
  multiStoreOperatorIds: [],
  storeIds: [],
  valuesAs: REVENUE_VALUE
}

export default {
  components: {
    iInstructions,
    iCustomiseSalesReport,
    iOverlay,
    iProgressLinear,
    iSalesReportExclusionsInfo
  },

  data: () => ({
    aggregationPeriods: AGGREGATION_PERIODS,
    customiseReportDialog: false,
    defaultDateTo: moment(),
    defaultDateFrom: moment().subtract(DAYS_BACK, 'days'),
    loading: true,
    exporting: false,
    initialLoading: true,
    reportData: [],
    reportFields: REPORT_FIELDS,
    tableHeadersFixed: [
      {
        text: 'TRADING DEPARTMENT',
        value: 'tradingDepartment',
        sortable: true,
        class: 'trading-department'
      },
      {
        text: 'CATEGORY',
        value: 'category',
        sortable: true,
        class: 'category'
      },
      {
        text: 'SUB CATEGORY',
        value: 'subCategory',
        sortable: true,
        class: 'sub-category'
      },
      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemId',
        sortable: true,
        class: 'supplier-item-id'
      },
      {
        text: 'BRAND',
        value: 'brand',
        sortable: true,
        class: 'brand'
      },
      {
        text: 'PRODUCT DESCRIPTION',
        value: 'productDescription',
        sortable: true,
        class: 'product-description'
      },
      {
        text: 'GRAND TOTAL',
        value: 'grandTotal',
        align: 'center',
        sortable: true,
        class: 'grand-total'
      }
    ],
    tableHeadersVariable: [],
    tableItemsVariable: [],
    tableItemsGrandTotal: {}
  }),

  computed: {
    ...mapGetters('tenant', ['tenantCurrency']),
    dateFromDisplay() {
      return this.$options.filters.formatDatePicker(this.reportFields.dateFrom)
    },

    dateToDisplay() {
      return this.$options.filters.formatDatePicker(this.reportFields.dateTo)
    },

    tableHeaders() {
      return [].concat(this.tableHeadersFixed, this.tableHeadersVariable)
    },

    tableItems() {
      return this.tableItemsVariable
    },

    valuesAsRevenue() {
      return REVENUE_VALUE
    },

    valuesAsItems() {
      return [
        { text: 'Carton Quantity', value: CARTONS_VALUE },
        { text: `Revenue (${this.tenantCurrency})`, value: REVENUE_VALUE }
      ]
    },

    valuesAsTooltip() {
      return `Carton Quantity or Revenue (${this.tenantCurrency})`
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    // **
    // Functions related to data table generation
    // **

    generateReportTable(tableData, valuesAs) {
      this.tableHeadersVariable = this.generatedHeadersVariable(
        tableData,
        valuesAs
      )
      this.tableItemsVariable = this.generatedItemsVariable(tableData, valuesAs)
      this.tableItemsGrandTotal = this.generatedItemsGrandTotal(
        tableData,
        valuesAs
      )
    },

    generatedHeadersVariable(tableData) {
      let headers = []

      for (const period in tableData.grandTotalsByPeriod) {
        headers = [
          ...headers,
          {
            text: period.toUpperCase(),
            value: period,
            sortable: true,
            align: 'center',
            class: 'aggregation-period'
          }
        ]
      }

      return headers
    },

    generatedItemsVariable(dataTable, valuesAs) {
      return dataTable.lines.map((line) => {
        // If values as Revenue, need to convert to Number format
        const valuesByPeriod =
          valuesAs === this.valuesAsRevenue
            ? this.convertObjectValuesToNumber(line.valuesByPeriod)
            : line.valuesByPeriod

        const grandTotal =
          valuesAs === this.valuesAsRevenue
            ? this.formatNumber(line.grandTotal)
            : line.grandTotal

        const updatedProductDescription = line.isFreeProduct
          ? `${line.product?.tradeItemDescription} - Free`
          : line.product?.tradeItemDescription

        return {
          supplierItemId: line.product.supplierItemId,
          brand: line.product.brand,
          productDescription: updatedProductDescription,
          grandTotal,
          ...line.product.categoryProductHierarchy,
          ...valuesByPeriod
        }
      })
    },

    generatedItemsGrandTotal(tableData, valuesAs) {
      // If values as Revenue, need to convert to Number format
      const grandTotalByPeriod =
        valuesAs === this.valuesAsRevenue
          ? this.convertObjectValuesToNumber(tableData.grandTotalsByPeriod)
          : tableData.grandTotalsByPeriod

      const grandTotal =
        valuesAs === this.valuesAsRevenue
          ? this.formatNumber(tableData.grandTotal)
          : tableData.grandTotal

      return {
        tradingDepartment: 'GRAND TOTAL',
        grandTotal,
        ...grandTotalByPeriod
      }
    },

    // **
    //  Helper functions
    //  **

    convertObjectValuesToNumber(object) {
      const newObject = {}

      for (const key in object) {
        newObject[key] = this.formatNumber(object[key])
      }

      return newObject
    },

    formatDate(moment) {
      return moment.format('YYYY-MM-DD')
    },

    formatNumber(value) {
      return this.$options.filters.currency(value)
    },

    // **
    // Functions related to manipulating data
    // **

    handleAggregationPeriod(period) {
      this.reportFields.aggregationPeriod = period
      this.fetchReportData()
    },

    handleValuesAs(as) {
      this.reportFields.valuesAs = as
      this.fetchReportData()
    },

    setPredefinedData() {
      this.reportFields.dateTo = this.formatDate(this.defaultDateTo)
      this.reportFields.dateFrom = this.formatDate(this.defaultDateFrom)
      this.reportFields.aggregationPeriod = AGGREGATION_PERIODS.filter(
        (p) => p.value === 'MONTH'
      )[0]?.value
      this.reportFields.valuesAs = REVENUE_VALUE
    },

    // **
    // Functions related to fetching data
    // **

    customiseReport(customisedReportFields) {
      this.reportFields = { ...customisedReportFields }
      this.customiseReportDialog = false
      this.fetchReportData()
    },

    fetchReportData() {
      this.loading = true
      const payload = { ...this.reportFields }

      fetchSalesByProducts(payload)
        .then((res) => {
          this.reportData = res
          this.generateReportTable(this.reportData, this.reportFields.valuesAs)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Reports data could not be updated...'
          })
        })
        .finally(() => {
          this.loading = this.initialLoading = false
        })
    },

    async exportReport() {
      const payload = { ...this.reportFields }
      this.loading = true
      this.exporting = true
      await fetchSalesByProductsAsExcel(payload)
        .then((response) => {
          const timestamp = moment().format('YYYYMMDD_HHmmss')
          const filename = `sales-by-product_${timestamp}.xlsx`
          const file = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(file)
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({ color: 'error', defaultError: true })
        })

      this.loading = false
      this.exporting = false
    }
  },

  mounted() {
    this.setPredefinedData()
    this.fetchReportData()
  }
}
</script>

<style lang="scss">
#sales-by-products {
  .p-report-table {
    th.trading-department,
    th.supplier-item-id,
    th.supplier-name {
      width: 160px !important;
      min-width: 160px !important;
      max-width: 160px !important;
    }
    th.category,
    th.sub-category {
      width: 125px !important;
      min-width: 125px !important;
      max-width: 125px !important;
    }
    th.brand {
      width: 70px !important;
      min-width: 70px !important;
      max-width: 70px !important;
    }
    th.product-description {
      width: 220px !important;
      min-width: 220px !important;
      max-width: 220px !important;
    }
    th.grand-total {
      text-align: center !important;
      width: 150px !important;
      min-width: 150px !important;
      max-width: 150px !important;
      color: black !important;
    }
    th.aggregation-period {
      min-width: 125px !important;
      text-align: center !important;
    }
    th.grand-total,
    td:nth-child(7) {
      border-right: 2px solid $primary-color;
      background-color: $primary-color-light;
    }
    tr:last-child td {
      background-color: $primary-color-light !important;
      border-top: 2px solid $primary-color;
      font-weight: bold;
      text-align: center;
    }
  }
}
</style>

<style lang="scss" scoped>
#sales-by-products {
  .p-customise-card {
    .p-list-div {
      display: flex;
      overflow: visible;
      height: 48px;
      .p-list-div-icon {
        align-items: center;
        background-color: $primary-color;
        border-radius: 5px 0px 0px 5px;
        display: flex;
        min-width: 48px;
        text-align: center;
        .p-list-icon {
          color: white;
          margin: auto;
          text-align: center;
          &:before {
            font-size: 24px;
          }
        }
      }
    }
  }
}
</style>
